import { Box, Button, Card, DataList, Flex, Separator } from '@radix-ui/themes';
import React, { useMemo } from 'react';
import { TextBlockSkeleton } from '../Skeletons/TextBlockSkeleton';
import { Title } from '../Title';
import { TruncatedTooltip } from '../TruncatedTooltip';
import { Trans, useTranslation } from 'react-i18next';

export interface DataEntry {
    label: string;
    value: React.ReactNode;
}

export interface MetadataCardProps {
    title: string;
    data: DataEntry[];
    columns: number;
    rows: number;
    isLoading: boolean;
}

export const MetadataCard = ({
    title,
    data,
    columns = 2,
    rows = 4,
    isLoading = false,
}: MetadataCardProps) => {
    const [isExpanded, setIsExpanded] = React.useState(false);

    useTranslation('common');

    // const maxItems = columns * rows;
    // const isOverflowing = data.length > maxItems;

    const partitionedData = useMemo(() => {
        const result: DataEntry[][] = Array.from({ length: columns }, () => []);

        data.forEach((entry, index) => {
            const columnIndex = index % columns;
            result[columnIndex].push(entry);
        });

        return result.filter((column) => column.length > 0);
    }, [columns, data]);

    return (
        <Card variant={'surface'} size={'5'} asChild data-radius={'none'}>
            <Flex direction={'column'} width={'max-content'}>
                <Title>{title}</Title>

                <Flex gap={'8'} justify={'between'}>
                    {partitionedData.map((columnData, columnIndex) => {
                        const filteredColumnData = isExpanded
                            ? columnData
                            : columnData.slice(0, rows);

                        return (
                            <React.Fragment key={columnIndex}>
                                <DataList.Root orientation={'vertical'}>
                                    {filteredColumnData.map(
                                        ({ label, value }, rowIndex) => (
                                            <Box
                                                width={'300px'}
                                                key={`${columnIndex}-${rowIndex}`}
                                            >
                                                <TextBlockSkeleton
                                                    loading={isLoading}
                                                    size={1}
                                                >
                                                    <DataList.Item>
                                                        <DataList.Label minWidth='88px'>
                                                            {label}
                                                        </DataList.Label>

                                                        <DataList.Value>
                                                            <TruncatedTooltip>
                                                                {value ?? '-'}
                                                            </TruncatedTooltip>
                                                        </DataList.Value>
                                                    </DataList.Item>
                                                </TextBlockSkeleton>
                                            </Box>
                                        ),
                                    )}
                                </DataList.Root>

                                {columnIndex < partitionedData.length - 1 && (
                                    <Box>
                                        <Separator
                                            orientation={'vertical'}
                                            size={'4'}
                                        />
                                    </Box>
                                )}
                            </React.Fragment>
                        );
                    })}
                </Flex>

                {/* {isOverflowing && ( */}
                <Flex justify={'end'} mt={'4'}>
                    <Button
                        variant={'ghost'}
                        color='gray'
                        onClick={() => setIsExpanded(!isExpanded)}
                        disabled={isLoading}
                    >
                        <Trans
                            i18nKey={
                                isExpanded
                                    ? 'common:pagination.seeLess'
                                    : 'common:pagination.seeMore_other'
                            }
                            values={{ itemCount: data.length }}
                        />
                    </Button>
                </Flex>
                {/* )} */}
            </Flex>
        </Card>
    );
};
